import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import useBranch from 'client/hooks/useBranch';
import analytics from 'client/analytics';
import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrButton, { SR_BUTTON_THEME_ADOBE_BLUE, SR_BUTTON_FONT_SIZE_SMALL, SR_BUTTON_HEIGHT_SHORT } from 'client/components/SrButton/SrButton';
import { CAMPAIGN, getAnnouncementContent, isAnnouncementPreview, getActor, getPmsEligibility, isCampaignActive, readState, writePlacement, isActorStorageKey, canShowPlacement, updatePlacement, shouldDeferSlideOut, shouldObserveObstructions } from './announcementState';
import AnnouncementImagePreview from './AnnouncementImagePreview';
import AnnouncementCloseButton from './AnnouncementCloseButton';
import observeVisibleImpression from './observeVisibleImpression';
import getAnnouncementReturnTarget from './getAnnouncementReturnTarget';
import './UPAnnouncements.less';

const AnnouncementContext = createContext(null);

export function UPAnnouncementProvider({ user, partner, children }) {
  const actor = getActor(user);
  const actorId = actor?.id;
  const { disabled, appUrl } = useBranch({ disabled: ['env', 'PARTNER_CONNECT_ANNOUNCEMENT_DISABLED'], appUrl: ['env', 'APP_URL'] });
  const preview = isAnnouncementPreview({ isUpAdmin: actor?.isUpAdmin, appUrl, search: typeof window === 'undefined' ? '' : window.location.search });
  const content = getAnnouncementContent(partner?.propertyManagementSoftware?.name);
  const [state, setState] = useState({});
  const [ready, setReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [slideOpen, setSlideOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const stateRef = useRef({});
  const seenThisVisit = useRef(new Set());
  const pmsEligibility = getPmsEligibility(partner?.propertyManagementSoftware?.name);
  const enabled = Boolean(ready && actorId && partner?.id && pmsEligibility !== 'unsupported' && isCampaignActive(disabled, now));

  const observeObstructions = shouldObserveObstructions({ enabled, state, isUpAdmin: actor?.isUpAdmin, seenThisVisit: seenThisVisit.current, slideOpen });

  useEffect(() => {
    let saved = {};
    try { if (!preview) saved = readState(window.localStorage, actorId); } catch (error) { /* Storage can be unavailable in private browsers. */ }
    stateRef.current = saved;
    seenThisVisit.current = new Set();
    setState(saved);
    setSlideOpen(false);
    setReady(Boolean(actorId));
    const sync = (event) => {
      if (preview || !isActorStorageKey(event.key, actorId)) return;
      const next = readState(window.localStorage, actorId);
      stateRef.current = next;
      setState(next);
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [actorId, preview]);

  useEffect(() => {
    if (!observeObstructions) return undefined;
    const check = () => {
      setLightboxOpen(Boolean(document.querySelector('[data-announcement-lightbox][open]')));
      setModalOpen(shouldDeferSlideOut({
        hasModal: Boolean(document.querySelector('.darkModal, [aria-modal="true"]:not([data-announcement-lightbox])')) || document.body.classList.contains('overflow-hidden'),
        viewportWidth: window.innerWidth,
        supportPanelHeight: window.innerWidth <= 600
          ? document.querySelector('#hubspot-messages-iframe-container')?.getBoundingClientRect().height || 0 : 0,
      }));
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-modal', 'style', 'open'] });
    window.addEventListener('resize', check);
    return () => { observer.disconnect(); window.removeEventListener('resize', check); };
  }, [observeObstructions]);

  useEffect(() => {
    if (enabled && !modalOpen && !state.slide_out?.shown && !state.slide_out?.dismissed) setSlideOpen(true);
  }, [enabled, modalOpen, state.slide_out]);

  useEffect(() => {
    const remaining = Date.parse(CAMPAIGN.endsAt) - now;
    if (remaining <= 0) return undefined;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.min(remaining + 1, 2147483647));
    return () => window.clearTimeout(timer);
  }, [now]);

  const save = useCallback((placement, patch) => {
    let next = updatePlacement(stateRef.current, placement, patch);
    try {
      if (!preview) {
        writePlacement(window.localStorage, actorId, placement, patch);
        next = { ...next, ...readState(window.localStorage, actorId) };
      }
    } catch (error) { /* Keep the current visit usable when storage is blocked. */ }
    stateRef.current = next;
    setState(next);
  }, [actorId, preview]);

  const track = (event, placement, extra = {}) => {
    if (preview) return;
    analytics.track(event, {
      campaignId: CAMPAIGN.id,
      userId: actorId,
      partnerId: partner.id,
      partnerType: partner.customerType || null,
      pmsType: partner.propertyManagementSoftware?.name || null,
      pmsEligibility,
      placement,
      isUpAdmin: actor.isUpAdmin,
      ...extra,
    });
  };
  const show = (placement) => enabled && canShowPlacement(state, placement, actor.isUpAdmin, seenThisVisit.current.has(placement));
  const impression = (placement) => {
    if (seenThisVisit.current.has(placement)) return;
    seenThisVisit.current.add(placement);
    const count = (stateRef.current[placement]?.impressions || 0) + 1;
    save(placement, { shown: true, impressions: count });
    track('extension_announcement_impression', placement);
    if (placement === 'slide_out') track('extension_announcement_opened', placement);
  };
  const dismiss = (placement) => {
    save(placement, { dismissed: true });
    if (placement === 'slide_out') setSlideOpen(false);
    track('extension_announcement_dismissed', placement, { impressionsBeforeDismiss: stateRef.current[placement]?.impressions || 0 });
  };
  return (
    <AnnouncementContext.Provider value={{ show, impression, dismiss, track, content, modalOpen, lightboxOpen }}>
      {children}
      {slideOpen && !modalOpen && <UPAnnouncement placement="slide_out" animate={!seenThisVisit.current.has('slide_out')} />}
    </AnnouncementContext.Provider>
  );
}

export default function UPAnnouncement({ placement, animate = false }) {
  const context = useContext(AnnouncementContext);
  const element = useRef(null);
  const impressed = useRef(false);
  const recordImpression = useRef(context?.impression);
  recordImpression.current = context?.impression;
  const [imageOpen, setImageOpen] = useState(false);
  const visible = Boolean(context?.show(placement));
  const obscured = context?.lightboxOpen || (placement === 'top_bar' && context?.modalOpen);
  const isSlide = placement === 'slide_out';
  const isContextual = placement === 'modal_bar';

  useEffect(() => {
    if (!visible || imageOpen || obscured || !element.current || impressed.current) return undefined;
    return observeVisibleImpression(element.current, () => {
      impressed.current = true;
      recordImpression.current(placement);
    });
  }, [visible, imageOpen, obscured, placement]);

  if (!visible) return null;
  const close = () => {
    const container = element.current;
    const hadFocus = container?.contains(document.activeElement);
    const target = getAnnouncementReturnTarget(container, placement);
    context.dismiss(placement);
    if (hadFocus && target && !container.contains(target)) target.focus({ preventScroll: true });
  };
  const handleKeyDown = (event) => {
    if (isSlide && !imageOpen && event.key === 'Escape') { event.preventDefault(); close(); }
  };
  const connectLink = (
    <a href={CAMPAIGN.url} target="_blank" rel="noopener noreferrer" styleName="cta" onClick={() => context.track('extension_announcement_cta_clicked', placement)}>
      Connect for Free<span styleName="srOnly"> (opens in a new tab)</span>
    </a>
  );
  const dismissButton = <AnnouncementCloseButton onClick={close} />;
  return (
    <section
      ref={element}
      styleName={isSlide ? `slide${animate ? ' slideEnter' : ''}` : `bar${isContextual ? ' contextualBar' : ''}`}
      data-testid={`extension-announcement-${placement}`}
      role={isSlide ? 'dialog' : 'region'}
      aria-label={isSlide ? undefined : 'Utility Profit Connect announcement'}
      aria-labelledby={isSlide ? 'up-connect-announcement-title' : undefined}
      onKeyDown={handleKeyDown}
    >
      {isSlide && (
        <header styleName="slideHeader">
          <div styleName="slideToolbar">{dismissButton}</div>
          <h2 id="up-connect-announcement-title" styleName="slideTitle">{context.content.headline}</h2>
        </header>
      )}
      <SrCard
        styleName={isSlide ? 'slideCard' : 'barCard'}
        padding={isSlide ? '0 var(--announcement-inset) var(--announcement-inset)' : '12px 16px'}
        margin="0"
        maxWidth="100%"
      >
        <div styleName={isSlide ? 'slideContent' : 'barContent'}>
          <div styleName={isSlide ? 'slideContent' : 'messageAndAction'}>
            <div styleName="copy" data-testid="extension-announcement-message">
              {isSlide ? (
                <>
                  <SrText font="greycliff" size="18" weight="500" color="blueGreycliff" display="block" lineHeight="28px">
                    Check setup status, providers, and account numbers without switching between dashboards.
                  </SrText>
                  {context.content.image ? (
                    <button
                      type="button"
                      styleName="illustration"
                      aria-label={`Enlarge ${context.content.examplePms} example`}
                      aria-haspopup="dialog"
                      onClick={() => setImageOpen(true)}
                    >
                      <img
                        styleName="previewImage"
                        src={context.content.image}
                        alt={`Example of Utility Profit utility details inside ${context.content.examplePms}`}
                        width="1200"
                        height="800"
                      />
                      <span styleName="illustrationLabel">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M6 2H2v4M10 2h4v4M2 10v4h4m8-4v4h-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Enlarge preview
                      </span>
                    </button>
                  ) : null}
                  <SrText font="greycliff" size="18" weight="500" color="blueGreycliff" display="block" lineHeight="28px" margin="20px 0 12px">
                    {context.content.pms
                      ? 'Included with your Utility Profit account.'
                      : 'Works with AppFolio, Buildium, and Rent Manager.'}
                  </SrText>
                </>
              ) : (
                <SrText font="greycliff" size="16" weight="600" color="blueGreycliff" lineHeight="24px">
                  {isContextual ? context.content.modalBar : context.content.topBar}
                </SrText>
              )}
            </div>
            {isSlide ? (
              <div styleName="slideActions">
                <button type="button" styleName="notNow" onClick={close}>Not now</button>
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_BLUE}
                  fontSizeType={SR_BUTTON_FONT_SIZE_SMALL}
                  heightType={SR_BUTTON_HEIGHT_SHORT}
                  minWidth="112px"
                  themeOverrides={{ padding: '0 24px', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1.2' }}
                  onClick={() => {
                    context.track('extension_announcement_cta_clicked', placement);
                    window.open(CAMPAIGN.url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  Connect for Free<span styleName="srOnly"> (opens in a new tab)</span>
                </SrButton>
              </div>
            ) : connectLink}
          </div>
          {!isSlide && dismissButton}
        </div>
      </SrCard>
      {isSlide && imageOpen && <AnnouncementImagePreview image={context.content.image} pms={context.content.examplePms} onClose={() => setImageOpen(false)} />}
    </section>
  );
}
